import { useEffect, useMemo, useState } from "react";
import { getCodesWithCache } from "./getCodesWithCache.js";

/**

 * @param {string} code
 * @param {string|null} subgroup
 */
export const useDropdownOptions = (code, subgroup = null) => {
  const [codesData, setCodesData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let mounted = true;

    const fetchData = async () => {
      try {
        setIsLoading(true);
        const res = await getCodesWithCache(code, subgroup);
        if (mounted) setCodesData(res || null);
      } catch (err) {
        console.error(`Failed to load dropdown: ${code}`, err);
        if (mounted) setCodesData(null);
      } finally {
        if (mounted) setIsLoading(false);
      }
    };

    fetchData();
    return () => (mounted = false);
  }, [code, subgroup]);

  const options = useMemo(() => {
    if (!codesData) return [];
    if (Array.isArray(codesData.CONFIG)) {
      return codesData.CONFIG.filter((opt) => opt.CODE || opt.TEXT).map(
        (opt) => ({
          value: opt.CODE || opt.TEXT,
          label: opt.TEXT || opt.CODE,
        })
      );
    }

    // Case 2: array of { value, label }
    if (
      Array.isArray(codesData) &&
      codesData.every((opt) => "value" in opt && "label" in opt)
    ) {
      return codesData.map((opt) => ({
        value: opt.value,
        label: opt.label,
      }));
    }

    // Case 3: array of strings
    if (
      Array.isArray(codesData) &&
      codesData.every((opt) => typeof opt === "string")
    ) {
      return codesData.map((opt) => ({ value: opt, label: opt }));
    }

    // fallback
    console.warn(`Unexpected dropdown format for ${code}:`, codesData);
    return [];
  }, [codesData, code]);

  return {
    options,
    isLoading,
    raw: codesData,
  };
};
